import { Text, Flex, Portal, Box, Separator, Popover, Skeleton } from '@chakra-ui/react';
import type { UserItemProps } from '@halo/ui-chat-react';
import { UserItem } from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';
import IconMention from './icons/mention.svg?react';

export interface ReadUserProps extends UserItemProps {
  user_id: string;
  mention?: boolean;
}

export interface ReadUsersState {
  readUsers: ReadUserProps[];
  unreadUsers: ReadUserProps[];
}

export interface MessagingStatusReadOverlayProps {
  requestUsers: (cb: (data: ReadUsersState) => void) => Promise<(() => void) | void>;
  children: React.ReactNode;
  hideMember?: boolean;
}

export interface ReadUsersProps {
  users: ReadUserProps[];
  type: string;
  loading: boolean;
}

export function ReadUsers({ users, type, loading }: ReadUsersProps) {
  return (
    <Box w="50%" flex="1 1 auto">
      <Flex gap="1" p="2.5" align="center">
        <Skeleton loading={loading}>
          <Text fontSize="lg" fontWeight="semibold" lineHeight="normal">
            {users.length}
          </Text>
          <Text fontSize="sm" fontWeight="medium" color="fg.muted">
            {type}
          </Text>
        </Skeleton>
      </Flex>
      <Box className="scrollHover" h="200px" overflowY="auto">
        <Skeleton loading={loading}>
          <Flex direction="column">
            {users.map((item) => (
              <UserItem
                key={item.user_id}
                {...item}
                avatarProps={{ icon: item.mention && <IconMention /> }}
              ></UserItem>
            ))}
          </Flex>
        </Skeleton>
      </Box>
    </Box>
  );
}

export const MessagingStatusReadOverlay = memo(function MessagingStatusReadOverlay({
  children,
  requestUsers,
  hideMember,
}: MessagingStatusReadOverlayProps) {
  const { t } = useTranslation();
  const refRemoveListener = useRef<(() => void) | void>(() => {});
  const [loading, setLoading] = useState(false);
  const [{ readUsers, unreadUsers }, setState] = useState<ReadUsersState>({
    readUsers: [],
    unreadUsers: [],
  });

  const request = useCallback(async () => {
    setLoading(true);
    const removeListener = await requestUsers((data) => {
      setState(data);
    });
    setLoading(false);
    return removeListener;
  }, [requestUsers]);

  return (
    <Popover.Root
      positioning={{ placement: 'top-start' }}
      lazyMount
      onOpenChange={async ({ open }) => {
        if (open) {
          refRemoveListener.current = await request();
        } else {
          refRemoveListener.current?.();
        }
      }}
    >
      {!hideMember ? <Popover.Trigger cursor="pointer">{children}</Popover.Trigger> : children}
      <Portal>
        <Popover.Positioner>
          <Popover.Content p="1" w="500px">
            <Flex>
              <ReadUsers users={readUsers} type={t('chat.groupMessageRead')} loading={loading} />
              <Separator orientation="vertical" borderColor="grey.64" />
              <ReadUsers
                users={unreadUsers}
                type={t('chat.groupMessageUnread')}
                loading={loading}
              />
            </Flex>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
});
